import { ChangeDetectionStrategy, Component, Input, OnDestroy } from '@angular/core'
import { combineLatest, ReplaySubject } from 'rxjs'
import { filter, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators'

import { trackById } from '../../../../shared/utility-functions/track-by-id.utility'
import {
  calculateEndDate,
  getEventAnalyticsProperties,
  getEventVideosText,
  getEventVideosUrl,
  mapInterDayStatus,
  mapLiveEventStatusToDisplayTextClass
} from '../../functions'
import { checkEventPartnerMatchesDrillThrough } from '../../check-event-partner-matches-drill-through.function'
import { getEventPartner } from '../../get-event-partner.function'
import { notNullOrUndefined } from '../../../../shared/functions/type-guards'
import { safeDateString } from '../../../../shared/utility-functions/date.utility'
import { FloGeoEvent } from 'src/app/events/interfaces/flo-geo-event.interface'
import { AnalyticsProperties } from '../../../../shared/analytics/models/analytics-properties.interface'
import { AuthService } from '../../../../singleton-services/auth/auth.service'
import {
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
  EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION
} from '../../../../shared/experimentation/experiment.model'
import { ExperimentationService } from '../../../../shared/experimentation/experimentation.service'
import { VerticalService } from '../../../../singleton-services/vertical.service'

@Component({
  selector: 'flo-event-list-item',
  templateUrl: './event-list-item.component.html',
  styleUrls: ['./event-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventListItemComponent implements OnDestroy {
  @Input()
  set props(props: EventListItemComponentProps) {
    if (props) {
      this.props$.next(props)
    }
  }
  constructor(
    private readonly authService: AuthService,
    private readonly experimentationService: ExperimentationService,
    private readonly verticalService: VerticalService
  ) {}

  public readonly isNextGenEventHub$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_VARIATION,
    false
  )
  public readonly isNextGenEventHubShort$ = this.experimentationService.isInVariation(
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT,
    EXP_NEXT_GEN_EVENT_HUB_WRESTLING_SHORT_VARIATION,
    false
  )
  private readonly siteSetting = this.verticalService.siteSettings$

  private readonly props$ = new ReplaySubject<EventListItemComponentProps>(1)
  private readonly ngOnDestroy$ = new ReplaySubject<void>(1)
  public readonly pausedSubscription$ = this.authService.user$.pipe(map(user => !!user && user.pausedSubscription))
  public readonly event$ = this.props$.pipe(
    map(props => {
      const event = props.event
      const startDate = event.live_event?.start_date_time ?? event.start_date_time
      return {
        ...event,
        sponsor: event.node?.supporters.filter(supporter => supporter.supporter_type === 'title_sponsor')[0],
        partner: !checkEventPartnerMatchesDrillThrough(event) && getEventPartner(event),
        startDate: startDate !== undefined ? safeDateString(startDate) : undefined,
        endDate: startDate !== undefined ? calculateEndDate(startDate, event.number_of_days) : undefined,
        displayDateVariant: event.live_event?.status !== 'PRE-AIR' ? 2 : 3
      }
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )
  public readonly isNextGenEventHub = combineLatest([this.isNextGenEventHub$, this.isNextGenEventHubShort$]).pipe(
    switchMap(async ([isNextGenEventHub, isNextGenEventHubShort]) => isNextGenEventHub || isNextGenEventHubShort),
    takeUntil(this.ngOnDestroy$)
  )
  // this is returning an object and
  public readonly siteUrl = this.siteSetting.pipe(map(siteSettings => siteSettings.core_site_url))

  private readonly liveEvent$ = this.event$.pipe(
    map(event => event.live_event),
    filter(notNullOrUndefined),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  public readonly analytics$ = this.props$.pipe(map(props => props.analyticsProps))

  public readonly analyticsProperties$ = this.props$.pipe(
    map(props => ({
      status: props.event.live_event?.status,
      liveId: props.event.live_event?.id,
      eventId: props.event.id,
      itemOrder: props.index
    })),
    map(mappedProps =>
      getEventAnalyticsProperties(mappedProps.eventId, mappedProps.liveId, mappedProps.status, mappedProps.itemOrder)
    ),
    takeUntil(this.ngOnDestroy$)
  )

  public thumbnailImageUrl$ = this.event$.pipe(
    filter(event => event.live_event?.status === 'LIVE' && !!event.asset?.url),
    map(event => event.asset?.url)
  )

  public readonly linkText$ = combineLatest([this.event$, this.pausedSubscription$]).pipe(
    map(([event, pausedSubscription]) => {
      return pausedSubscription ? 'Resume' : getEventVideosText(event.replay_video_count, event.live_event?.status)
    }),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )
  public readonly routerLink$ = combineLatest([this.event$, this.pausedSubscription$]).pipe(
    map(([event, pausedSubscription]) =>
      pausedSubscription
        ? `/account/subscriptions`
        : getEventVideosUrl(event.slug_uri, event.live_event?.status, event.live_event?.id)
    )
  )

  public readonly name$ = this.linkText$.pipe(map(linkText => (/replay/i.test(linkText) ? 'Replays' : 'Watch')))

  public readonly eventStatus$ = this.liveEvent$.pipe(
    map(liveEvent => mapInterDayStatus(liveEvent.start_at_next, liveEvent.start_date_time, liveEvent.status)),
    takeUntil(this.ngOnDestroy$),
    shareReplay(1)
  )

  public readonly displayText$ = this.eventStatus$.pipe(map(status => (status === 'LIVE' ? 'Live Now' : undefined)))

  public readonly displayTextClass$ = this.eventStatus$.pipe(map(mapLiveEventStatusToDisplayTextClass))

  public readonly trackById = trackById

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}

/** @deprecated uses a deprecated type. Refer to flo-types or write a new interface. */
type Event = Pick<
  FloGeoEvent,
  | 'id'
  | 'live_event'
  | 'is_geoblocked'
  | 'slug_uri'
  | 'asset'
  | 'replay_video_count'
  | 'short_title'
  | 'start_date_time'
  | 'end_date_time'
  | 'drill_through_link'
  | 'node'
  | 'number_of_days'
>

export interface EventListItemComponentProps {
  readonly event: Event
  readonly index: number
  readonly analyticsProps: EventListItemAnalytics
}

/**
 * @deprecated This is missing the index for the item.
 *
 * @see ListItemEventAnalytics
 */
export interface EventListItemAnalytics extends AnalyticsProperties {
  name: string
  node_id: number
  item_order: number
  live_id?: number | null
  event_status?: string | null
}

/**
 * @deprecated This uses a deprecated arg type. Use the modernized version
 * instead.
 *
 * @see getEventAnalytics
 * @see {@link https://flocasts.atlassian.net/browse/FLO-14462}
 */
// tslint:disable:variable-name
export function getEventListItemAnalytics(event: Event, item_order: number): EventListItemAnalytics {
  return {
    name: event.short_title || 'no title',
    node_id: event.id || -1,
    live_id: event.live_event && event.live_event.id,
    event_status: event.live_event && event.live_event.status,
    item_order
  }
}

/**
 * @deprecated This function is being used incorrectly in EventFilterList.
 *
 * @see {@link https://flocasts.atlassian.net/browse/FLO-14462}
 */
export const getEventListItemComponentProps = (
  event: Event,
  analyticsProps: EventListItemAnalytics,
  index: number,
  isGeoblocked?: boolean
): EventListItemComponentProps => ({
  event: {
    id: event.id,
    short_title: event.short_title,
    live_event: event.live_event,
    slug_uri: event.slug_uri,
    asset: event.asset,
    start_date_time: event.start_date_time,
    end_date_time: event.end_date_time,
    replay_video_count: event.replay_video_count,
    drill_through_link: event.drill_through_link,
    node: event.node,
    is_geoblocked: isGeoblocked || event.is_geoblocked || false,
    number_of_days: event.number_of_days
  },
  analyticsProps,
  index
})
